import { useState, KeyboardEvent, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Send, Paperclip, Lock, Search, ShieldCheck, ShieldOff } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { QuickReplies } from './QuickReplies';
import { FilePreview } from './FilePreview';
import { AudioRecordButton } from './AudioRecordButton';
import { UserMenuButton } from './UserMenuButton';
import { useToast } from '@/hooks/use-toast';
import { validateFilesBatch } from '@/services/uploadQueue';
import { useFileExtraction, AttachedFile } from '@/hooks/useFileExtraction';

interface ChatInputBarProps {
  onSendMessage: (message: string, files?: AttachedFile[]) => void;
  disabled?: boolean;
  isLimitReached?: boolean;
  remaining?: number;
  onUpgradeClick?: () => void;
  onLegalSearch?: () => void;
  variant?: 'funcionarios' | 'oposiciones';
  anonymizationEnabled?: boolean;
  onToggleAnonymization?: () => void;
  showAnonymizationToggle?: boolean;
}

export interface ChatInputBarRef {
  setMessageWithQuote: (text: string) => void;
}

export const ChatInputBar = forwardRef<ChatInputBarRef, ChatInputBarProps>(({
  onSendMessage,
  disabled,
  isLimitReached = false,
  remaining,
  onUpgradeClick,
  onLegalSearch,
  variant = 'funcionarios',
  anonymizationEnabled = false,
  onToggleAnonymization,
  showAnonymizationToggle = false
}, ref) => {
  const [message, setMessage] = useState('');
  const { attachedFiles, addFiles, removeFile, clearAll, allReady } = useFileExtraction();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  // Expose method to set message with quote
  useImperativeHandle(ref, () => ({
    setMessageWithQuote: (text: string) => {
      const quotedText = `"${text}" `;
      setMessage(quotedText);
      setTimeout(() => {
        textareaRef.current?.focus();
        if (textareaRef.current) {
          textareaRef.current.selectionStart = quotedText.length;
          textareaRef.current.selectionEnd = quotedText.length;
        }
        adjustTextareaHeight();
      }, 0);
    }
  }));

  const isInputDisabled = disabled || isLimitReached;

  // Auto-resize textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 128)}px`;
    }
  };

  // Send is disabled while any file is still extracting
  const canSend = (message.trim() || attachedFiles.length > 0) && !isInputDisabled && allReady;

  const handleSend = () => {
    if (!canSend) return;
    const readyFiles = attachedFiles.filter(f => f.status === 'ready' || (f.status as string) === 'pending_upload' || f.status === undefined);
    const errorFiles = attachedFiles.filter(f => f.status === 'error');

    if (errorFiles.length > 0) {
      toast({
        title: 'Archivos excluidos',
        description: `${errorFiles.length} archivo(s) no pudieron procesarse y fueron excluidos`,
      });
    }

    const fileMessage = readyFiles.length > 0
      ? `📄 ${readyFiles.length} documento${readyFiles.length > 1 ? 's' : ''} adjunto${readyFiles.length > 1 ? 's' : ''}`
      : '';
    onSendMessage(message.trim() || fileMessage, readyFiles.length > 0 ? readyFiles : undefined);
    setMessage('');
    clearAll();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const validationError = validateFilesBatch(files, attachedFiles.length);
    if (validationError) {
      toast({
        title: 'Error de validación',
        description: validationError,
        variant: 'destructive',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    addFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleQuickReplySelect = (text: string) => {
    if (text === 'Buscar jurisprudencia' && onLegalSearch) {
      onLegalSearch();
      return;
    }
    setMessage(text);
    setTimeout(() => {
      textareaRef.current?.focus();
      adjustTextareaHeight();
    }, 0);
  };

  return (
    <div className="border-t border-[#002B5B]/10 bg-white p-2 sm:p-4 space-y-2 sm:space-y-3">

      <div className="max-w-4xl mx-auto space-y-3">
        {/* Limit Reached Banner */}
        {isLimitReached && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3 bg-amber-50 border border-amber-200 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3">
            <div className="flex items-center gap-2 min-w-0">
              <Lock className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs sm:text-sm text-amber-800">
                Has alcanzado tu límite de <strong>20 consultas gratuitas</strong>. Actualiza tu plan para continuar.
              </p>
            </div>
            {onUpgradeClick && (
              <button
                onClick={onUpgradeClick}
                className="flex-shrink-0 w-full sm:w-auto px-4 py-1.5 bg-gradient-to-r from-[#002B5B] to-[#06D3AA] text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity"
              >
                Mejorar plan
              </button>
            )}
          </div>
        )}

        {/* Quick Replies */}
        {variant !== 'oposiciones' && (
          <QuickReplies onSelect={handleQuickReplySelect} disabled={isInputDisabled} variant={variant} />
        )}

        {/* File Previews */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachedFiles.map((af) => (
              <FilePreview
                key={af.id}
                name={af.file.name}
                size={af.file.size}
                type={af.file.type}
                previewUrl={af.file.type.startsWith('image/') ? URL.createObjectURL(af.file) : undefined}
                onRemove={() => removeFile(af.id)}
                extractionStatus={af.status}
                extractionError={af.errorMessage}
                pageCount={af.pageCount}
              />
            ))}
          </div>
        )}

        {/* Input Area */}
        <div className="flex items-end gap-1.5 sm:gap-2">
          {/* User Menu - hidden on mobile (available in drawer) */}
          <div className="hidden sm:block">
            <UserMenuButton variant={variant} />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xlsx,.xls,.wav,.mp4,image/jpeg,image/png,image/webp,audio/wav,audio/x-wav,audio/mpeg,video/mp4,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={handleFileSelect}
            multiple
            className="hidden"
          />
          <button
            onClick={() => !isInputDisabled && fileInputRef.current?.click()}
            className="p-2 sm:p-2.5 rounded-lg hover:bg-[#E2F1EF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative shrink-0"
            title={isLimitReached ? 'Límite alcanzado' : `Adjuntar archivos (${attachedFiles.length}/10)`}
            disabled={isInputDisabled || attachedFiles.length >= 10}
          >
            <Paperclip className="h-5 w-5 text-[#002B5B]/60" />
            {attachedFiles.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#06D3AA] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {attachedFiles.length}
              </span>
            )}
          </button>
          <AudioRecordButton
            onTranscription={(text) => {
              setMessage(prev => prev ? `${prev} ${text}` : text);
              setTimeout(() => {
                textareaRef.current?.focus();
                adjustTextareaHeight();
              }, 0);
            }}
            disabled={isInputDisabled}
          />
          {variant !== 'oposiciones' && (
            <button
              onClick={() => !isInputDisabled && onLegalSearch?.()}
              className="p-2.5 rounded-lg hover:bg-[#E2F1EF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Buscar jurisprudencia"
              disabled={isInputDisabled || !onLegalSearch}
            >
              <Search className="h-5 w-5 text-[#002B5B]/60" />
            </button>
          )}
          {showAnonymizationToggle && (
            <button
              onClick={onToggleAnonymization}
              className={`p-2.5 rounded-lg transition-colors ${
                anonymizationEnabled
                  ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-600'
                  : 'hover:bg-[#E2F1EF] text-[#002B5B]/60'
              }`}
              title={anonymizationEnabled ? 'Anonimización activada (LOPDGDD)' : 'Anonimización desactivada'}
            >
              {anonymizationEnabled ? (
                <ShieldCheck className="h-5 w-5" />
              ) : (
                <ShieldOff className="h-5 w-5" />
              )}
            </button>
          )}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                adjustTextareaHeight();
              }}
              onKeyDown={handleKeyDown}
              placeholder={isLimitReached ? 'Límite de consultas alcanzado' : 'Escribe tu consulta jurídica municipal…'}
              disabled={isInputDisabled}
              className={`w-full min-h-[48px] sm:min-h-[56px] max-h-32 p-3 sm:p-4 pr-12 sm:pr-14 rounded-2xl bg-white border resize-none focus:outline-none focus:ring-2 text-sm leading-relaxed text-[#002B5B] disabled:opacity-50 disabled:cursor-not-allowed ${isLimitReached
                ? 'border-amber-300 placeholder:text-amber-500 focus:ring-amber-300'
                : 'border-[#002B5B]/20 placeholder:text-[#002B5B]/40 focus:ring-[#06D3AA]'
                }`}
              rows={1}
            />
            <Button
              onClick={handleSend}
              disabled={!canSend}
              size="sm"
              className={`absolute right-2 bottom-2 transition-opacity text-white ${isLimitReached
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#002B5B] to-[#06D3AA] hover:opacity-90'
                }`}
            >
              {isLimitReached ? <Lock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Advertencia institucional — Art. 50 RIA (UE 2024/1689) */}
      <div className="max-w-4xl mx-auto">
        <p className="text-xs text-[#6B7280] text-center px-4 leading-relaxed">
          Munilex utiliza Inteligencia Artificial para generar respuestas de carácter orientativo.
          Verifica siempre la normativa aplicable con las fuentes oficiales (BOE, CENDOJ, DGT…).
          La IA puede cometer errores: contrasta la información antes de usarla profesionalmente.
        </p>
      </div>
    </div>
  );
});

ChatInputBar.displayName = 'ChatInputBar';
